import { useState } from "react";
import { CaPageHeader, usePath, friendlyTime } from "@/company-admin/shared";
import { Button, Field, Input, Select, TextArea, Modal, StatusPill } from "@/admin/ui";
import DataTable from "@/admin/DataTable";
import { caApi, formatApiError } from "@/company-admin/api";

const ROLES = ["ceo", "sales", "finance", "hr", "operations", "marketing", "customer", "procurement", "legal", "strategy", "meeting"];

const empty = { name: "", email: "", role: "sales", department: "", message: "" };

export default function Invitations() {
    const { data, loading, refresh } = usePath("/company-admin/invitations");
    const departments = usePath("/company-admin/departments");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    async function send() {
        setBusy(true); setErr("");
        try {
            await caApi.post("/company-admin/invitations", form);
            setOpen(false);
            setForm(empty);
            await refresh();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function resend(i) {
        try {
            await caApi.post(`/company-admin/invitations/${i.id}/resend`);
            await refresh();
        } catch (e) { alert(formatApiError(e)); }
    }
    async function cancel(i) {
        if (!confirm(`Cancel invitation to ${i.email}?`)) return;
        try {
            await caApi.delete(`/company-admin/invitations/${i.id}`);
            await refresh();
        } catch (e) { alert(formatApiError(e)); }
    }
    function copyLink(inv) {
        const link = `${window.location.origin}/invitations/${inv.token}`;
        navigator.clipboard.writeText(link).then(() => alert(`Activation link copied:\n${link}`));
    }

    const cols = [
        {
            key: "name", label: "Invitee",
            render: (r) => (
                <div>
                    <div className="font-semibold text-black">{r.name || "—"}</div>
                    <div className="text-[12px] text-neutral-500">{r.email}</div>
                </div>
            ),
        },
        {
            key: "role", label: "Role",
            render: (r) => <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2.5 py-1">{r.role}</span>,
        },
        { key: "department", label: "Department", render: (r) => r.department || "—" },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
        { key: "sent_at", label: "Sent", render: (r) => <span className="text-[12px] text-neutral-500">{friendlyTime(r.sent_at)}</span> },
        { key: "expires_at", label: "Expires", render: (r) => <span className="text-[12px] text-neutral-500">{friendlyTime(r.expires_at)}</span> },
        {
            key: "actions", label: "",
            render: (r) => (
                <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="subtle" onClick={() => copyLink(r)} data-testid={`inv-copy-${r.id}`}>Copy link</Button>
                    {r.status === "sent" && <Button size="sm" variant="ghost" onClick={() => resend(r)} data-testid={`inv-resend-${r.id}`}>Resend</Button>}
                    {r.status !== "cancelled" && r.status !== "accepted" && (
                        <Button size="sm" variant="danger" onClick={() => cancel(r)} data-testid={`inv-cancel-${r.id}`}>Cancel</Button>
                    )}
                </div>
            ),
            tdClass: "text-right",
        },
    ];

    return (
        <div data-testid="ca-invitations-page">
            <CaPageHeader
                title="Invitations"
                subtitle="Invite employees. They set their own password on the activation link and are auto-routed to the right workspace."
                action={<Button size="lg" onClick={() => { setForm(empty); setOpen(true); }} data-testid="inv-send-btn">+ Send Invitation</Button>}
            />

            <DataTable cols={cols} rows={data || []} rowKey={(r) => r.id} loading={loading} empty={{ title: "No invitations yet", hint: "Send your first invitation." }} />

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Send Invitation"
                size="lg"
                testid="inv-modal"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={send} disabled={busy} data-testid="inv-modal-send">
                            {busy ? "Sending…" : "Send Invitation"}
                        </Button>
                    </>
                }
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Employee Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></Field>
                    <Field label="Email"><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" /></Field>
                    <Field label="Role">
                        <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="inv-role">
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </Select>
                    </Field>
                    <Field label="Department">
                        <Select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                            <option value="">— None —</option>
                            {(departments.data || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                        </Select>
                    </Field>
                    <div className="sm:col-span-2">
                        <Field label="Personal Message">
                            <TextArea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Welcome to CEO's GPT!" />
                        </Field>
                    </div>
                    {err && <div className="sm:col-span-2 rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">{err}</div>}
                </div>
            </Modal>
        </div>
    );
}
